import type { SVGProps } from 'react';

/**
 * Minimal inline stroke-icon set (generic geometric primitives, 24x24,
 * currentColor) — avoids adding an icon-library dependency for a couple
 * dozen glyphs. Every icon used anywhere in the app shell/dashboards comes
 * from this one file so visual weight/stroke width stays consistent.
 */
type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function base(props: IconProps) {
  const { size = 18, ...rest } = props;
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    ...rest,
  };
}

export const IconDashboard = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="3" y="3" width="8" height="8" rx="1.5" />
    <rect x="13" y="3" width="8" height="5" rx="1.5" />
    <rect x="13" y="10" width="8" height="11" rx="1.5" />
    <rect x="3" y="13" width="8" height="8" rx="1.5" />
  </svg>
);

export const IconBox = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M21 8l-9-5-9 5 9 5 9-5z" />
    <path d="M3 8v8l9 5 9-5V8" />
    <path d="M12 13v8" />
  </svg>
);

export const IconTruck = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="1" y="6" width="13" height="10" rx="1.2" />
    <path d="M14 10h4l3 3v3h-7z" />
    <circle cx="5.5" cy="18" r="1.6" />
    <circle cx="17.5" cy="18" r="1.6" />
  </svg>
);

export const IconUsers = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="8.5" cy="8" r="3.2" />
    <path d="M2.5 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
    <circle cx="17" cy="8.5" r="2.6" />
    <path d="M15.2 14.8c2.7.3 4.8 2.3 4.8 5.2" />
  </svg>
);

export const IconFileText = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M6 2.5h8l4 4V21a1 1 0 01-1 1H6a1 1 0 01-1-1V3.5a1 1 0 011-1z" />
    <path d="M14 2.5V7h4" />
    <path d="M8 12h8M8 16h8M8 8.5h3" />
  </svg>
);

export const IconClipboardCheck = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="5" y="4" width="14" height="17" rx="1.5" />
    <path d="M9 4V3a1 1 0 011-1h4a1 1 0 011 1v1" />
    <path d="M9 13l2 2 4-4.5" />
  </svg>
);

export const IconLayers = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 2l9 5-9 5-9-5 9-5z" />
    <path d="M3 12l9 5 9-5" />
    <path d="M3 17l9 5 9-5" />
  </svg>
);

export const IconRepeat = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M4 8h13l-3-3M20 16H7l3 3" />
  </svg>
);

export const IconTrash = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M4 7h16M9 7V4.5A1.5 1.5 0 0110.5 3h3A1.5 1.5 0 0115 4.5V7" />
    <path d="M6 7l1 13a1.5 1.5 0 001.5 1.4h7a1.5 1.5 0 001.5-1.4l1-13" />
  </svg>
);

export const IconArchive = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="3" y="3.5" width="18" height="4.5" rx="1.2" />
    <path d="M5 8v10.5A1.5 1.5 0 006.5 20h11a1.5 1.5 0 001.5-1.5V8" />
    <path d="M10 12.5h4" />
  </svg>
);

export const IconWallet = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 7.5A2.5 2.5 0 015.5 5H18a2 2 0 012 2v1" />
    <rect x="2.5" y="7.5" width="19" height="13" rx="2" />
    <path d="M16.5 14.2a1.3 1.3 0 100-2.6 1.3 1.3 0 000 2.6z" />
  </svg>
);

export const IconClock = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" />
  </svg>
);

export const IconAlertTriangle = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M10.6 3.5a1.6 1.6 0 012.8 0l8.5 15A1.6 1.6 0 0120.5 21H3.5a1.6 1.6 0 01-1.4-2.5l8.5-15z" />
    <path d="M12 9v5M12 17.2h.01" />
  </svg>
);

export const IconShield = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 2.5l8 3v6c0 5-3.4 8.5-8 10-4.6-1.5-8-5-8-10v-6l8-3z" />
    <path d="M9 11.8l2 2 4-4.3" />
  </svg>
);

export const IconTrendUp = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3 17l6-6 4 4 8-8" />
    <path d="M15 6h6v6" />
  </svg>
);

export const IconCalendar = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="3" y="4.5" width="18" height="16" rx="1.8" />
    <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
  </svg>
);

export const IconSettings = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M19.4 13.5a1.8 1.8 0 00.36 2l.06.06a2.2 2.2 0 11-3.1 3.1l-.07-.06a1.8 1.8 0 00-2-.36 1.8 1.8 0 00-1.1 1.66V20a2.2 2.2 0 01-4.4 0v-.1a1.8 1.8 0 00-1.18-1.65 1.8 1.8 0 00-2 .36l-.07.06a2.2 2.2 0 11-3.1-3.1l.06-.07a1.8 1.8 0 00.36-2 1.8 1.8 0 00-1.66-1.1H2.2a2.2 2.2 0 010-4.4h.1A1.8 1.8 0 003.95 6.8a1.8 1.8 0 00-.36-2l-.06-.07a2.2 2.2 0 113.1-3.1l.07.06a1.8 1.8 0 002 .36H8.8A1.8 1.8 0 0010 .4V.2a2.2 2.2 0 014.4 0v.1A1.8 1.8 0 0015.5 2a1.8 1.8 0 002-.36l.07-.06a2.2 2.2 0 113.1 3.1l-.06.07a1.8 1.8 0 00-.36 2v.1a1.8 1.8 0 001.66 1.1h.2a2.2 2.2 0 010 4.4h-.1a1.8 1.8 0 00-1.6 1.1z" />
  </svg>
);

export const IconSearch = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

export const IconBell = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M18 8a6 6 0 10-12 0c0 6-2.5 8-2.5 8h17S18 14 18 8z" />
    <path d="M10.3 21a1.7 1.7 0 003.4 0" />
  </svg>
);

export const IconHelp = (p: IconProps) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9.5" />
    <path d="M9.3 9.2a2.7 2.7 0 015.2 1c0 1.8-2.5 2-2.5 3.7" />
    <path d="M12 17.3h.01" />
  </svg>
);

export const IconMenu = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M3.5 6.5h17M3.5 12h17M3.5 17.5h17" />
  </svg>
);

export const IconChevronDown = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export const IconLogOut = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M9 21H5.5A1.5 1.5 0 014 19.5v-15A1.5 1.5 0 015.5 3H9" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </svg>
);

export const IconBuilding = (p: IconProps) => (
  <svg {...base(p)}>
    <rect x="4" y="3" width="16" height="18" rx="1.2" />
    <path d="M8 7.5h1.6M14.4 7.5H16M8 11.5h1.6M14.4 11.5H16M8 15.5h1.6M14.4 15.5H16" />
    <path d="M10 21v-4h4v4" />
  </svg>
);

export const IconChartBar = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M4 20V10M11 20V4M18 20v-7" />
    <path d="M2.5 20.5h19" />
  </svg>
);

export const IconSparkles = (p: IconProps) => (
  <svg {...base(p)}>
    <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3z" />
    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15z" />
  </svg>
);
